import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Trash2, MessageCircle, ExternalLink, Phone, Copy, Play, AlertCircle } from "lucide-react";

function TemplateButton({ template, setTemplate }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [buttons, setButtons] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // URL validation function
  const validateURL = (url, urlType) => {
    if (!url || url.trim() === '') {
      return {
        isValid: false,
        error: "URL is required"
      };
    }

    // Valid prefixes
    const validPrefixes = ['http://', 'https://', 'whatsapp-smb://'];
    const hasValidPrefix = validPrefixes.some(prefix => url.toLowerCase().startsWith(prefix));

    if (!hasValidPrefix) {
      return {
        isValid: false,
        error: "You need to enter a valid URL. Check for typos and make sure that your URL begins with http://, https:// or whatsapp-smb://"
      };
    }

    // Basic URL format validation
    try {
      if (url.toLowerCase().startsWith('whatsapp-smb://')) {
        // Basic validation for whatsapp-smb URLs
        if (url.length < 15) {
          return {
            isValid: false,
            error: "Invalid WhatsApp SMB URL format"
          };
        }
      } else {
        // For http and https, use URL constructor for validation
        new URL(url);
      }
    } catch (error) {
      return {
        isValid: false,
        error: "You need to enter a valid URL. Check for typos and make sure that your URL begins with http://, https:// or whatsapp-smb://"
      };
    }

    // Dynamic URL specific validation
    if (urlType === 'Dynamic') {
      const dynamicParamPattern = /\{\{\d+\}\}/;
      if (!dynamicParamPattern.test(url)) {
        return {
          isValid: false,
          error: "Dynamic URLs should contain parameters like {{1}}, {{2}}, etc."
        };
      }

      // Check for valid parameter numbering (should start from 1)
      const params = url.match(/\{\{(\d+)\}\}/g);
      if (params) {
        const paramNumbers = params.map(p => parseInt(p.match(/\d+/)[0]));
        const minParam = Math.min(...paramNumbers);
        if (minParam !== 1) {
          return {
            isValid: false,
            error: "Dynamic parameters should start from {{1}}"
          };
        }
      }
    }

    // Static URL should not contain dynamic parameters
    if (urlType === 'Static') {
      const hasDynamicParams = /\{\{\d+\}\}/.test(url);
      if (hasDynamicParams) {
        return {
          isValid: false,
          error: "Static URLs should not contain dynamic parameters like {{1}}, {{2}}"
        };
      }
    }

    return {
      isValid: true,
      error: null
    };
  };


  const addButton = (buttonType) => {
    if (buttons.length >= 10) return;

    let baseType = buttonType;
    let subtype = null;

    if (buttonType === "URL_WEBSITE") {
      baseType = "URL";
      subtype = "VISIT_WEBSITE";
    }

    if (buttonType === "URL_WHATSAPP") {
      baseType = "URL";
      subtype = "WHATSAPP_CHAT";
    }

    const newButton = {
      type: baseType,
      subtype,
      id: Date.now(),
      text: getDefaultText(baseType, subtype),
      url: "",
      phone_number: "",
      action: getDefaultAction(baseType, subtype),
      urlType: "Static"
    };

    setButtons([...buttons, newButton]);
  };

  const getDefaultText = (type) => {
    switch (type) {
      case 'QUICK_REPLY': return 'Custom response';
      case 'URL': return 'Visit website';
      case 'PHONE_NUMBER': return 'Call Phone';
      case 'VOICE_CALL': return 'Call on WhatsApp';
      default: return 'Button';
    }
  };





  const getDefaultAction = (type) => {
    switch (type) {
      case 'PHONE_NUMBER': return 'Call Phone Number';
      case 'VOICE_CALL': return 'Call on WhatsApp';
      case 'URL': return 'Visit website';
      default: return 'Custom';
    }
  };





  const removeButton = (id) => {
    setButtons(buttons.filter((b) => b.id !== id));
  };

  // const updateButton = (id, field, value) => {
  //   setButtons(buttons.map(btn =>
  //     btn.id === id ? { ...btn, [field]: value } : btn
  //   ));
  // };



  const updateButton = (id, field, value) => {
    setButtons(buttons.map(btn =>
      btn.id === id
        ? {
          ...btn,
          [field]: value,
          // prevent subtype corruption
          ...(field === "type" ? { subtype: null } : {})
        }
        : btn
    ));
  };

  const getURLValidation = (btn) => {
    if (btn.type !== 'URL') return { isValid: true, error: null };
    return validateURL(btn.url, btn.urlType);
  };


  useEffect(() => {
    setTemplate(prev => {
      let newComponents = prev.components.filter(c => c.type !== "BUTTONS");

      if (buttons.length > 0) {
        const formattedButtons = buttons.map(btn => {
          const buttonData = { text: btn.text || "Button" };

          switch (btn.type) {
            case "PHONE_NUMBER":
              buttonData.type = "PHONE_NUMBER";
              buttonData.phone_number = btn.phone_number || "";
              break;
            case "URL":
              buttonData.type = "URL";
              buttonData.url = btn.url || "";
              if ((buttonData.url.match(/\{\{\d*\}\}/g) || []).length > 0) {
                buttonData.example = btn.example || "example-value";
              }
              break;

            case "VOICE_CALL":
              buttonData.type = "VOICE_CALL";
              buttonData.phone_number = btn.phone_number || "";
              break;
            default:
              buttonData.type = "QUICK_REPLY";
          }
          return buttonData;
        });

        newComponents = [...newComponents, { type: "BUTTONS", buttons: formattedButtons }];
      }

      const newTemplate = { ...prev, components: [...newComponents] };



      return newTemplate;
    });
  }, [buttons, setTemplate]);



  const buttonTypes = [
    {
      type: "QUICK_REPLY",
      icon: MessageCircle,
      title: "Custom",
      desc: "Create a custom response for customers to choose"
    },
    {
      type: "URL",
      icon: ExternalLink,
      title: "Visit website",
      desc: "Add a button that opens a website"
    },

    {
      type: "VOICE_CALL",
      icon: MessageCircle,
      title: "Chat on WhatsApp",
      desc: "Add a button to make a WhatsApp call",
      iconColor: "text-green-600"
    },
    {
      type: "PHONE_NUMBER",
      icon: Phone,
      title: "Call Phone Number",
      desc: "Add a button to make a phone call"
    }
  ];

  return (
    <div className="mb-2 bg-white p-6 rounded-lg border">
      <h3 className="font-semibold mb-2 ml-4 "> Buttons <span className="text-md text-gray-400">.Optional</span></h3>
      <p className="text-sm text-gray-600 mb-4">
        Create buttons that let customers respond to your message or take
        action. You can add up to ten buttons.
      </p>

      {/* Add Button Dropdown */}
      <div className="relative mb-6" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-300 rounded hover:bg-blue-50 font-medium text-sm"
        >
          + Add button
          <ChevronDown
            className={`w-4 h-4 ml-2 transition-transform ${isDropdownOpen ? "rotate-180" : ""
              }`}
          />
        </button>

        {isDropdownOpen && (
          <div
            className={`absolute z-10 w-72 bg-white rounded-md shadow-lg border py-2 ${buttons.length < 3 ? "bottom-full mb-2" : "top-full mt-1"
              }`}
          >
            <div className="px-4 py-2 text-xs text-gray-500 font-medium border-b">
              SELECT BUTTON TYPE
            </div>

            {buttonTypes.map(({ type, icon: Icon, title, desc, iconColor = "text-gray-600" }) => (
              <button
                key={type}
                type="button"
                onClick={() => addButton(type)}
                className="flex items-start w-full text-left px-4 py-3 hover:bg-gray-50 gap-3"
              >
                <div className="w-6 h-6 mt-0.5 flex-shrink-0">
                  <Icon size={20} className={iconColor} />
                </div>
                <div>
                  <div className="font-medium text-gray-900">{title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Button Configurations */}
      <div className="space-y-4">
        {buttons.map((btn) => {
          const urlValidation = getURLValidation(btn);

          return (
            <div key={btn.id} className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                <div className="flex items-center gap-2">
                   <span className="font-medium text-gray-700">
                    {btn.type === "QUICK_REPLY"
                      ? "Quick reply"
                      : btn.type === "URL"
                      ? "Call to action"
                      : btn.type === "PHONE_NUMBER"
                      ? "Call Phone Number"
                      : btn.type === "VOICE_CALL"
                      ? "WhatsApp Button"
                      :""}
                  </span>
                  {btn.type === "QUICK_REPLY" && <span className="text-xs text-gray-400">• Optional</span>}
                </div>
                <button
                  type="button"
                  className="text-red-600 text-sm hover:text-red-700 flex items-center gap-1"
                  onClick={() => removeButton(btn.id)}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Quick Reply */}
              {btn.type === "QUICK_REPLY" && (
                <div className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                      value={btn.action}
                      onChange={(e) => updateButton(btn.id, "action", e.target.value)}
                    >
                      <option>Custom</option>
                      <option>Pre-configured response</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button text
                      <span className="text-gray-400 text-xs ml-1">{btn.text?.length || 0}/25</span>
                    </label>
                    <input
                      value={btn.text}
                      onChange={(e) => updateButton(btn.id, "text", e.target.value)}
                      placeholder="Quick Reply"
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      maxLength={25}
                    />
                  </div>
                </div>
              )}

              {/* URL */}
              {btn.type === "URL" && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type of action</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        value={btn.action}
                        onChange={(e) => updateButton(btn.id, "action", e.target.value)}
                      >
                        <option>Visit website</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Button text <span className="text-gray-400 text-xs ml-1">{btn.text?.length || 0}/25</span>
                      </label>
                      <input
                        value={btn.text}
                        onChange={(e) => updateButton(btn.id, "text", e.target.value)}
                        placeholder="Visit website"
                       
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                        maxLength={25}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">URL type</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        value={btn.urlType}
                        onChange={(e) => updateButton(btn.id, "urlType", e.target.value)}
                      >
                        <option>Static</option>
                        <option>Dynamic</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                      <div className="relative">
                        <input
                          value={btn.url}
                          onChange={(e) => updateButton(btn.id, "url", e.target.value)}
                          placeholder={btn.urlType === 'Dynamic' ? "https://example.com/{{1}}" : "https://"}
                          className={`w-full p-2 border rounded text-sm pr-8 ${btn.url && !urlValidation.isValid
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                            }`}
                        />
                        {btn.url && !urlValidation.isValid && (
                          <div className="absolute top-2 right-2">
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* URL Validation Error Message */}
                  {btn.url && !urlValidation.isValid && (
                    <div className="text-red-600 text-sm bg-red-50 p-3 rounded border border-red-200">
                      {urlValidation.error}
                    </div>
                  )}

                  {/* Dynamic URL Example Field */}
                  {btn.urlType === 'Dynamic' && btn.url && /\{\{\d+\}\}/.test(btn.url) && urlValidation.isValid && (
                    <div className="grid grid-cols-4 gap-4">
                      <div className="col-start-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Example value for {"{{1}}"}
                        </label>
                        <input
                          value={btn.example}
                          onChange={(e) => updateButton(btn.id, "example", e.target.value)}
                          placeholder="example-value"
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This helps preview how your dynamic URL will look
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`track-${btn.id}`}
                      checked={btn.trackConversions}
                      onChange={(e) => updateButton(btn.id, "trackConversions", e.target.checked)}
                    />
                    <label htmlFor={`track-${btn.id}`} className="text-sm text-gray-700">
                      Track app conversions (Marketing Messages Lite API only)
                    </label>
                  </div>
                </div>
              )}

              {/* Phone Number */}
              {btn.type === "PHONE_NUMBER" && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type of action</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        value={btn.action}
                        onChange={(e) => updateButton(btn.id, "action", e.target.value)}
                      >
                        <option>Call Phone Number</option>
                        <option>Call on WhatsApp</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button text</label>
                      <input
                        value={btn.text}
                        onChange={(e) => updateButton(btn.id, "text", e.target.value)}
                        placeholder="Call phone"
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        value={btn.country}
                        onChange={(e) => updateButton(btn.id, "country", e.target.value)}
                      >
                        <option>US +1</option>
                        <option>UK +44</option>
                        <option>CA +1</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
                      <input
                        value={btn.phone_number}
                        onChange={(e) => updateButton(btn.id, "phone_number", e.target.value)}
                        placeholder="0/20"
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                  {btn.phone_number === '' && (
                    <div className="text-red-500 text-sm">
                      You need to enter a phone number. Please add a valid phone number.
                    </div>
                  )}
                </div>
              )}


              {/* WhatsApp */}
              {btn.type === "VOICE_CALL" && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type of action</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        value={btn.action}
                        onChange={(e) => updateButton(btn.id, "action", e.target.value)}
                      >
                        <option>Call on WhatsApp</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Button text</label>
                      <input
                        value={btn.text}
                        onChange={(e) => updateButton(btn.id, "text", e.target.value)}
                        placeholder="Call on WhatsApp"
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Active for</label>
                      <select
                        className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        value={btn.activeFor}
                        onChange={(e) => updateButton(btn.id, "activeFor", e.target.value)}
                      >
                        <option>7 days</option>
                        <option>3 days</option>
                        <option>1 day</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                    <div className="w-4 h-4 mt-0.5 flex-shrink-0 bg-blue-500 rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-white rounded-full"></div>
                    </div>
                    <div className="text-sm text-blue-700">
                      Turn on calling in the{' '}
                      <a href="#" className="text-blue-600 underline">WhatsApp Manager portal</a>.
                      Alternatively, you can use the Phone Number Settings API.{' '}
                      <a href="#" className="text-blue-600 underline">About calling on WhatsApp</a>
                    </div>
                  </div>
                </div>
              )}




            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TemplateButton;